import { CommonModule, DatePipe } from "@angular/common";
import { Component, Inject, LOCALE_ID, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { catchError, tap } from "rxjs";
import { TaskbookService } from "../../../taskbook-triage/taskbook.service";
import { TmTaskbookModule } from "../../../taskbook-triage/taskbook-triage.module";
import { RouterLink } from "@angular/router";
import { TmPipesModule } from "../../../shared/util/pipes";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TimelinePollResultTimelineItemType } from "../../../timeline/timeline-item";
import { UserInfos } from "../../../shared/ui/username-icon/user-infos.type";
import { PollListItem, PollOccurrence, PollTrends } from "app/frontend/lib/polls/user-polls/user-polls.type";
import { format, getDaysInMonth } from "date-fns";
import { PollPreviewComponent } from "app/frontend/lib/polls/create-poll/poll-preview/poll-preview.component";
import { PollSetupService } from "app/frontend/lib/polls/polls-setup.service";
import { PollDetails } from "app/frontend/lib/polls/user-polls/poll-results-dialog/poll-results-dialog.component";
import { QuestionForPreview, RecurrenceType } from "app/frontend/lib/polls/create-poll/create-poll.type";
import { PollType } from "app/frontend/lib/polls/polls.type";
import { PollResultsChartComponent } from "app/frontend/lib/polls/user-polls/poll-results-chart/poll-results-chart.component";
import { PollTaskDetailsOccurenceComponent } from "../poll-task-details-occurrence/poll-task-details-occurrence.component";
import { MatTabsModule } from "@angular/material/tabs";
import { LineChartSerie, PollResultValue } from "app/frontend/lib/timeline/timeline-item/poll-result/poll-result.type";
import { PollResultCommentsComponent } from "app/frontend/lib/timeline/timeline-item/poll-result/poll-result-comments/poll-result-comments.component";
import { PollResultService } from "app/frontend/lib/timeline/timeline-item/poll-result/poll-result.service";
import { ITeam } from "@teammood/team";
import { RecurringPollResultTrendsLineChartComponent } from "app/frontend/lib/timeline/timeline-item/poll-result/recurring/recurring-poll-result-trends-line-chart/recurring-poll-result-trends-line-chart.component";
import { RecurringClosedPollResultTrendsChartComponent } from "app/frontend/lib/timeline/timeline-item/poll-result/recurring/recurring-closed-poll-result-trends-chart/recurring-closed-poll-result-trends-chart.component";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { FormsModule } from "@angular/forms";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { MatMenuModule } from "@angular/material/menu";
import { MakePollRecurrentFormComponent } from "app/frontend/lib/polls/user-polls/make-poll-recurrent-dialog/make-poll-reccurrent-form/make-poll-reccurrent-form.component";
import { TeamChipListComponent } from "../../team-chip-list/team-chip-list.component";
import { PollResultsLegendComponent } from "app/frontend/lib/polls/user-polls/poll-results-chart/poll-results-legend/poll-results-legend.component";
import { PollTaskDetailsOccurenceListComponent } from "../poll-task-details-occurrence-list/poll-task-details-occurrence-list.component";

@Component({
    selector: 'poll-task-details-dialog',
    templateUrl: './poll-task-details-dialog.component.html',
    styleUrls: ["./../../../polls/user-polls/user-polls.component.scss", "./poll-task-details-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatTabsModule,
    TmTaskbookModule,
    TmPipesModule,
    MatButtonToggleModule,
    RouterLink,
    FormsModule,
    MatTooltipModule,
    MatMenuModule,
    PollPreviewComponent,
    PollResultsChartComponent,
    PollTaskDetailsOccurenceListComponent,
    PollResultCommentsComponent,
    RecurringPollResultTrendsLineChartComponent,
    RecurringClosedPollResultTrendsChartComponent,
    MakePollRecurrentFormComponent,
    TeamChipListComponent,
    PollResultsLegendComponent
]
  })
  export class PollTaskDetailsDialogComponent implements OnInit {
  
    protected poll: PollListItem;
    protected pollResults: PollDetails;
    protected pollTrends: PollTrends;
    protected loading = true;
    protected threadInCreation = false;
    protected userInfos: UserInfos;
    protected datePipe: DatePipe;
    private locale: string;
    protected questionForPreview: QuestionForPreview;
    protected questionType: PollType;
    protected selectedOccurrence: PollOccurrence;
    protected frequencyOfRecurrence: RecurrenceType;
    protected context: 'backlog' | 'setup';
    //for trends
    protected pollDisplayMode: 'preview' |'results' | 'trends' = 'preview';
    protected dates: string[];
    protected teams: ITeam[];
    protected series: LineChartSerie[];
    protected allTeamsAverageSerie: LineChartSerie;    
  
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PollTaskDetailsDialogData,
        @Inject(LOCALE_ID) locale: string,
        private dialogRef: MatDialogRef<PollTaskDetailsDialogComponent>,
        private taskbookService: TaskbookService,
        protected translate: TranslateService,
        private pollSetupService: PollSetupService,
        private pollResultService: PollResultService,
        private snackBarService: SnackBarService
    ){
      this.locale = locale;
      this.datePipe = new DatePipe(this.locale);
    }
  
    ngOnInit(): void {
        const pollId = this.data.pollId;
        this.teams = this.data.teams;
        this.pollDisplayMode = this.data.mode;  
        this.context = this.data.context;      

        const closedTestId =  "e06dd7f1-bdce-4cd2-86c0-e86759315c9d";  // How would you rate your current workload?
        const starsTestId = "2f304483-b665-46fb-94c9-524651ea2235"; // How would you rate the team spirit?
        const agreeTestId =  "056160c0-6eb2-4e67-a628-773973cae5eb"; // How do you agree with the following statement: The general atmosphere is good, I feel comfortable at work. 
        const scaleId = "564cdb67-cbd1-4d19-b662-f7301b6148de" // How would you rate the level of our technical debt?

        this.taskbookService.pollTaskDetails(pollId).pipe(
            tap(taskData => {
                this.poll = taskData;
                this.questionForPreview = { label: taskData.label, choices: taskData.availableChoices}
                this.questionType = this.itemTypeToPollType(taskData.itemType);
                if(this.pollDisplayMode === 'results'){ 
                    const selectedOccurrenceIndex = this.poll.occurrences.findIndex((occurrence) => occurrence.pollOccurrenceId === this.data.resultOccurrenceId);
                    this.setSelectedOccurrence(this.poll.occurrences[selectedOccurrenceIndex]);
                }
                this.loading = false;
            })
        ).subscribe();
    }

    onPollDisplayModeChange(mode: 'preview' | 'results' | 'trends'){
        switch(mode){
            case 'preview':
                this.selectedOccurrence = undefined;
                break;
            case 'results':
                let currentOcc = this.selectedOccurrence ? this.selectedOccurrence : this.poll.occurrences[0].status === 'FinishedQuestionStatus' ? this.poll.occurrences[0] : this.poll.occurrences[1];
                this.setSelectedOccurrence(currentOcc);
                break;
            case 'trends':
                this.selectedOccurrence = undefined;
                this.getTrendPollResults(this.poll);
                break;
        }
    }

    setSelectedOccurrence(pollOccurrence: PollOccurrence){
        this.pollDisplayMode = 'results';
        this.selectedOccurrence = pollOccurrence;
        this.showPollResults(pollOccurrence);
    }

    showPollResults(pollOccurrence: PollOccurrence){
      this.pollResults = undefined;

      this.pollSetupService.getPollDetails(pollOccurrence.pollOccurrenceId).pipe(
        tap((pollDetails: PollDetails) => {
            this.pollResults = pollDetails;
            this.pollDisplayMode = 'results';
        }),
      ).subscribe();
    }

    protected async getTrendPollResults(poll: PollListItem){
        this.pollSetupService.getPollTrend(poll.id).pipe(
            tap((pollTrends: PollTrends) => {
                if (pollTrends.allResults) {
                    this.pollTrends = pollTrends;
                    this.dates = this.pollResultService.formatPollTrendResultDates(this.pollTrends.allResults.dates)
                    if (this.poll.itemType === 'closedQuestionResultItem') {
                        this.series = this.pollResultService.formatClosedPollTrendResult(this.pollTrends.allResults.values, this.teams)
                        this.allTeamsAverageSerie = this.series.length === 1 ? this.series[0] : this.pollResultService.formatClosedPollAverageTrendResult(this.series)
                    } else {
                        this.series = this.pollResultService.formatPollTrendResultSeries(this.pollTrends.allResults.values, this.teams)
                    }
                    this.pollDisplayMode = 'trends';
                }
            }),
        ).subscribe();
    }

    // start - put in a service
    protected getFrequencyStartDay(startDate: Date, type: string){
      switch(type){
          case 'weekly':
              return format(startDate, 'iiii');
          case 'monthly':
              return getDaysInMonth(startDate);
        }
    }
    protected commentCount(pollResults: PollResultValue[]): number {
        if (pollResults) {
            return pollResults.flatMap(pollResult => pollResult.comments).length;
        }
        return 0;
    }

    protected hasComments(pollResults: PollResultValue[]): boolean {
        if (pollResults) {
            return this.commentCount(pollResults) > 0;
        }
        return false;
    }

    itemTypeToPollType(itemType: TimelinePollResultTimelineItemType): PollType{
        switch(itemType){
            case 'agreeDisagreeQuestionResultItem':
                return 'AgreeDisagreeQuestion';
            case 'closedQuestionResultItem':
                return 'ClosedQuestion';
            case 'scaleQuestionResultItem':
                return 'ScaleQuestion';
            case 'fiveStarsQuestionResultItem':
                return 'FiveStarsQuestion';
        }
    }

    convertPollTypeIntoTimelineItemType(pollType: PollType): TimelinePollResultTimelineItemType {
        if (pollType) {
            switch (pollType) {
                case 'ClosedQuestion':
                    return 'closedQuestionResultItem';
                case 'AgreeDisagreeQuestion':
                    return 'agreeDisagreeQuestionResultItem';
                case 'ScaleQuestion':
                    return 'scaleQuestionResultItem';
                case 'FiveStarsQuestion':
                    return 'fiveStarsQuestionResultItem';
                default:
                    console.error('Unknown poll type');
            }
        }
    }
    // end - put in a service


    makePollRecurrent(frequency: RecurrenceType){
        this.frequencyOfRecurrence = frequency;
    }
    onDurationValueChanged(poll: PollListItem){
        this.poll = poll;
    }
    savePollReccurrence(){
        this.pollSetupService.makePollRecurrent(this.poll.id, this.frequencyOfRecurrence, this.poll.duration).pipe(
            catchError((err) => {
                this.snackBarService.error(JSON.stringify(err));
                throw err;
            }),
            tap(() => {
                this.snackBarService.success('polls_settings.make_recurrent_dialog.success');
                this.poll.recurrence.frequency = this.frequencyOfRecurrence;
                this.frequencyOfRecurrence = undefined;
                //todo reload poll info?
            })
        ).subscribe();
    }

    cancelPollReccurrence(){
        this.frequencyOfRecurrence = undefined;
    }

    close(){
      this.dialogRef.close();
    }

}

export interface PollTaskDetailsDialogData {
    pollId: string;
    teams: ITeam[];
    context: 'backlog' | 'setup';
    mode: 'preview' | 'results' | 'trends';
    resultOccurrenceId?: string;
}